/**
 * Infinite logo marquee for `.sliding-images`.
 *
 * The strip is authored in the block editor (a core group block with the
 * `sliding-images` additional class), so there is no server-side loop to
 * duplicate the logos. This enhancement wraps the logos in an inner track,
 * clones the set enough to fill the viewport, and lets CSS drive a seamless
 * linear scroll.
 *
 * Perf: the strip lives near the footer, so nothing heavy runs at page load.
 * An IntersectionObserver defers the (reflow-heavy) build until the strip
 * nears the viewport, and pauses the animation whenever it scrolls off-screen.
 * Users with `prefers-reduced-motion` keep the original static row.
 */
(function () {
  var SELECTOR = '.sliding-images';
  var DEFAULT_SPEED = 40; // px per second

  function appendClone(track, originals) {
    originals.forEach(function (node) {
      track.appendChild(node.cloneNode(true));
    });
  }

  // One-time: move the logos into an inner track and clone them enough to
  // cover the viewport across the full scroll distance.
  function buildTrack(el) {
    if (el.dataset.marqueeReady) return;

    var originals = Array.prototype.slice.call(el.children);
    if (!originals.length) return;
    el.dataset.marqueeReady = '1';

    var track = document.createElement('div');
    track.className = 'sliding-images__track';
    originals.forEach(function (node) { track.appendChild(node); });
    el.classList.add('is-marquee');
    el.appendChild(track);

    var setCount = originals.length;

    // Clone the set once so a shift of exactly one period is seamless.
    appendClone(track, originals);

    // Period = distance from copy 1's first item to copy 2's first item. This
    // includes the boundary gap, so the rhythm never stutters at the loop.
    var period = track.children[setCount].offsetLeft - track.children[0].offsetLeft;

    // Keep cloning until the track covers the viewport across the whole shift.
    while (track.scrollWidth < el.clientWidth + period) {
      appendClone(track, originals);
    }

    var speed = parseFloat(el.dataset.speed) || DEFAULT_SPEED;
    track.style.setProperty('--marquee-shift', period + 'px');
    track.style.setProperty('--marquee-duration', period / speed + 's');

    el._marqueeTrack = track;
    el._marqueeOriginals = originals;
    el._marqueePeriod = period;
  }

  function markActive(el, on) {
    if (el._marqueeTrack) el._marqueeTrack.classList.toggle('is-active', on);
  }

  // On resize the viewport can widen past our coverage; top up clones only.
  function topUpCoverage(el) {
    var track = el._marqueeTrack;
    if (!track) return;
    var need = el.clientWidth + el._marqueePeriod;
    while (track.scrollWidth < need) {
      appendClone(track, el._marqueeOriginals);
    }
  }

  function init() {
    var strips = document.querySelectorAll(SELECTOR);
    if (!strips.length) return;

    if (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return; // leave the static centered row untouched
    }

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(strips, function (el) {
        buildTrack(el);
        markActive(el, true);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        if (entry.isIntersecting) {
          buildTrack(el);       // one-time build on first approach
          markActive(el, true); // run the animation
        } else {
          markActive(el, false); // pause while off-screen
        }
      });
    }, { rootMargin: '200px 0px' });

    Array.prototype.forEach.call(strips, function (el) { io.observe(el); });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        Array.prototype.forEach.call(strips, topUpCoverage);
      }, 200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
